import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart, Area,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const KG_TO_LB = 2.20462;

const RANGES = [
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: 'ALL', label: 'All', days: null },
];

// Weight = gold "you" line; moving average green; BMI/body-fat in warm ochre
// tones. No teal/blue/purple.
const COLORS = {
  weight: '#FFB800',
  movingAvg: '#4FB98A',
  bmi: '#B87D00',
  bodyFat: '#F0791A',
};

const convertWeight = (value, unit) => {
  if (value == null) return null;
  return unit === 'lb' ? +(value * KG_TO_LB).toFixed(1) : +value.toFixed(1);
};

const WeightChart = () => {
  const { user, refreshUser } = useAuth();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const unit = user?.weight_unit || 'kg';
  const [range, setRange] = useState('ALL');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/weight');
        if (response.status === 200) setData(response.data);
      } catch (error) {
        toast.error('Failed to load weight data');
      } finally {
        setLoading(false);
      }
    };
    const setData = (d) => setAllData(d);
    fetchData();
  }, []);

  const toggleUnit = async () => {
    const newUnit = unit === 'kg' ? 'lb' : 'kg';
    try {
      await apiClient.patch('/api/auth/weight-unit', { weight_unit: newUnit });
      await refreshUser();
    } catch {
      toast.error('Failed to save weight unit preference');
    }
  };

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    const rangeConfig = RANGES.find(r => r.key === range);
    if (!rangeConfig?.days) return allData;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - rangeConfig.days);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    return allData.filter(d => d.date >= cutoffStr);
  }, [allData, range]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const weightVals = filteredData.filter(d => d.weight != null).map(d => convertWeight(d.weight, unit));
    const bmiVals = filteredData.filter(d => d.bmi != null).map(d => d.bmi);
    const bfVals = filteredData.filter(d => d.body_fat != null).map(d => d.body_fat);
    const latest = filteredData[filteredData.length - 1];
    const first = filteredData[0];
    return {
      latestWeight: convertWeight(latest.weight, unit),
      latestBmi: latest.bmi,
      latestBodyFat: latest.body_fat,
      latestDate: latest.date,
      minWeight: weightVals.length ? Math.min(...weightVals) : null,
      maxWeight: weightVals.length ? Math.max(...weightVals) : null,
      avgWeight: weightVals.length ? +(weightVals.reduce((a, b) => a + b, 0) / weightVals.length).toFixed(1) : null,
      change: weightVals.length >= 2
        ? (convertWeight(latest.weight, unit) - convertWeight(first.weight, unit)).toFixed(1)
        : null,
      avgBmi: bmiVals.length ? +(bmiVals.reduce((a, b) => a + b, 0) / bmiVals.length).toFixed(1) : null,
      avgBodyFat: bfVals.length ? +(bfVals.reduce((a, b) => a + b, 0) / bfVals.length).toFixed(1) : null,
      totalEntries: filteredData.length,
      hasBmi: bmiVals.length > 0,
      hasBodyFat: bfVals.length > 0,
    };
  }, [filteredData, unit]);

  const chartData = useMemo(() => {
    if (!filteredData.length) return [];

    const windowSize = filteredData.length > 90 ? 14 : 7;

    return filteredData.map((d, i) => {
      const window = filteredData.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.weight != null).map(w => convertWeight(w.weight, unit));
      const movingAvg = avgVals.length >= 2
        ? +(avgVals.reduce((a, b) => a + b, 0) / avgVals.length).toFixed(1)
        : null;

      return {
        date: d.date,
        weight: d.weight != null ? convertWeight(d.weight, unit) : null,
        movingAvg,
        bmi: d.bmi != null ? +d.bmi.toFixed(1) : null,
        bodyFat: d.body_fat != null ? +d.body_fat.toFixed(1) : null,
      };
    });
  }, [filteredData, unit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-500 dark:text-gray-400">
            No weight data available. Sync your Fitbit to see weight data.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const rangeConfig = RANGES.find(r => r.key === range);
    const days = rangeConfig?.days;
    if (days && days <= 90) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const WeightTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          {d.weight != null && (
            <p style={{ color: COLORS.weight }}>
              Weight: <span className="font-semibold">{d.weight} {unit}</span>
            </p>
          )}
          {d.movingAvg != null && (
            <p style={{ color: COLORS.movingAvg }}>
              Moving Avg: <span className="font-semibold">{d.movingAvg} {unit}</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  const BmiTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          {d.bmi != null && (
            <p style={{ color: COLORS.bmi }}>
              BMI: <span className="font-semibold">{d.bmi}</span>
            </p>
          )}
          {d.bodyFat != null && (
            <p style={{ color: COLORS.bodyFat }}>
              Body Fat: <span className="font-semibold">{d.bodyFat}%</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  const changeVal = stats?.change ? parseFloat(stats.change) : null;

  return (
    <div className="space-y-6">
      {/* Range Selector + Unit Toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-1 bg-gray-100 dark:bg-surface-dark-raised rounded-lg p-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                range === r.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {stats?.totalEntries} entries
          </span>
          <button
            onClick={toggleUnit}
            className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {unit === 'kg' ? 'kg → lb' : 'lb → kg'}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Latest Weight</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.weight }}>
              {stats?.latestWeight != null ? `${stats.latestWeight}` : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{unit}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Change</p>
            <p className={`text-2xl font-bold ${
              changeVal != null && changeVal < 0 ? 'text-green-500' :
              changeVal != null && changeVal > 0 ? 'text-red-400' :
              'text-gray-900 dark:text-white'
            }`}>
              {changeVal != null ? `${changeVal > 0 ? '+' : ''}${stats.change}` : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{unit}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">BMI</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.bmi }}>
              {stats?.latestBmi != null ? stats.latestBmi.toFixed(1) : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {stats?.avgBmi != null ? `Avg: ${stats.avgBmi}` : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Body Fat</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.bodyFat }}>
              {stats?.latestBodyFat != null ? `${stats.latestBodyFat.toFixed(1)}%` : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {stats?.avgBodyFat != null ? `Avg: ${stats.avgBodyFat}%` : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Weight Trend Line Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Weight Trend</CardTitle>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span>Min: {stats?.minWeight ?? '--'}</span>
              <span>Avg: {stats?.avgWeight ?? '--'}</span>
              <span>Max: {stats?.maxWeight ?? '--'}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={['dataMin - 2', 'dataMax + 2']}
                tickFormatter={(v) => `${v}`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={50}
                label={{ value: unit, angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: 11 } }}
              />
              <Tooltip content={<WeightTooltip />} />
              <Legend
                formatter={(value) => {
                  const map = { weight: 'Weight', movingAvg: 'Moving Avg' };
                  return map[value] || value;
                }}
                wrapperStyle={{ fontSize: 12 }}
              />
              {stats?.avgWeight && (
                <ReferenceLine
                  y={stats.avgWeight}
                  stroke="#9ca3af"
                  strokeDasharray="3 3"
                  label={{ value: `Avg ${stats.avgWeight}`, position: 'insideTopLeft', fill: '#9ca3af', fontSize: 11 }}
                />
              )}
              <Area
                dataKey="weight"
                stroke={COLORS.weight}
                strokeWidth={2}
                fill={COLORS.weight}
                fillOpacity={0.06}
                type="monotone"
                dot={chartData.length <= 60 ? { r: 3, fill: COLORS.weight } : false}
                connectNulls
                name="weight"
              />
              <Line
                dataKey="movingAvg"
                stroke={COLORS.movingAvg}
                strokeWidth={2}
                dot={false}
                name="movingAvg"
                type="monotone"
                connectNulls
                strokeDasharray="5 3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* BMI & Body Fat Charts */}
      {(stats?.hasBmi || stats?.hasBodyFat) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {stats?.hasBmi && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">BMI Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      interval={tickInterval}
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={{ stroke: '#4b5563' }}
                      tickLine={{ stroke: '#4b5563' }}
                    />
                    <YAxis
                      domain={['dataMin - 1', 'dataMax + 1']}
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={{ stroke: '#4b5563' }}
                      tickLine={{ stroke: '#4b5563' }}
                      width={36}
                    />
                    <Tooltip content={<BmiTooltip />} />
                    <ReferenceLine y={18.5} stroke="#22c55e" strokeDasharray="3 3" />
                    <ReferenceLine y={25} stroke="#eab308" strokeDasharray="3 3" />
                    <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="3 3" />
                    <Line
                      dataKey="bmi"
                      stroke={COLORS.bmi}
                      strokeWidth={2}
                      dot={chartData.length <= 60 ? { r: 3, fill: COLORS.bmi } : false}
                      type="monotone"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-4 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-green-500 inline-block"></span> 18.5
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-yellow-500 inline-block"></span> 25
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-red-500 inline-block"></span> 30
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {stats?.hasBodyFat && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Body Fat Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      interval={tickInterval}
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={{ stroke: '#4b5563' }}
                      tickLine={{ stroke: '#4b5563' }}
                    />
                    <YAxis
                      domain={['dataMin - 2', 'dataMax + 2']}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={{ stroke: '#4b5563' }}
                      tickLine={{ stroke: '#4b5563' }}
                      width={42}
                    />
                    <Tooltip content={<BmiTooltip />} />
                    <Line
                      dataKey="bodyFat"
                      stroke={COLORS.bodyFat}
                      strokeWidth={2}
                      dot={chartData.length <= 60 ? { r: 3, fill: COLORS.bodyFat } : false}
                      type="monotone"
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default WeightChart;
